import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { SakuraField } from "../components/SakuraField";

type Entry = {
  rank: number;
  id: number;
  name: string;
  avatar_preset: string | null;
  current_level: number;
  xp: number;
  is_me: boolean;
};

type LeaderboardData = {
  scope: "class" | "org";
  period: "weekly" | "alltime";
  entries: Entry[];
  my_rank: number | null;
  my_xp: number;
  total_members: number;
};

export function LeaderboardPage() {
  const { t } = useTranslation(["common", "learner"]);
  const [scope, setScope] = useState<"class" | "org">("class");
  const [period, setPeriod] = useState<"weekly" | "alltime">("weekly");

  const { data, isLoading } = useQuery({
    queryKey: ["leaderboard", scope, period],
    queryFn: () =>
      api<LeaderboardData>(`/api/learner/leaderboard?scope=${scope}&period=${period}`),
  });

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-sakura/10 via-white to-blue-50 px-3 sm:px-6 py-4 sm:py-6 overflow-hidden">
      <SakuraField />

      {/* Header */}
      <header className="relative z-30 flex items-center gap-3 mb-4 sm:mb-6 max-w-2xl mx-auto">
        <Link
          to="/"
          aria-label={t("learner:map")}
          className="bg-white/90 backdrop-blur rounded-full shadow-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-primary text-lg sm:text-xl font-bold hover:bg-white shrink-0"
        >
          ×
        </Link>
        <h1 className="text-xl sm:text-2xl font-bold text-primary">{t("learner:leaderboard")}</h1>
      </header>

      <div className="relative z-20 max-w-2xl mx-auto">
        {/* Scope tabs */}
        <div className="flex gap-2 mb-3 bg-white/60 backdrop-blur rounded-2xl p-1.5 shadow-sm">
          <TabButton active={scope === "class"} onClick={() => setScope("class")}>
            {t("learner:scope_class")}
          </TabButton>
          <TabButton active={scope === "org"} onClick={() => setScope("org")}>
            {t("learner:scope_org")}
          </TabButton>
        </div>

        {/* Period tabs */}
        <div className="flex gap-2 mb-4 sm:mb-6 bg-white/60 backdrop-blur rounded-2xl p-1.5 shadow-sm">
          <TabButton active={period === "weekly"} onClick={() => setPeriod("weekly")}>
            {t("learner:period_weekly")}
          </TabButton>
          <TabButton active={period === "alltime"} onClick={() => setPeriod("alltime")}>
            {t("learner:period_alltime")}
          </TabButton>
        </div>

        {/* My summary */}
        {data && (
          <div className="bg-white/90 backdrop-blur rounded-2xl shadow-lg px-4 py-3 mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted">{t("learner:your_rank")}</div>
              <div className="text-2xl font-bold text-primary leading-tight">
                {data.my_rank ? `${data.my_rank}` : "—"}
                <span className="text-sm text-muted ml-1">
                  / {data.total_members}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted">XP</div>
              <div className="text-2xl font-bold text-primary leading-tight">{data.my_xp}</div>
            </div>
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <div className="text-center text-muted py-12">{t("common:loading")}</div>
        ) : !data || data.entries.length === 0 ? (
          <div className="bg-white/90 rounded-2xl shadow text-center text-muted py-12 px-4">
            {t("learner:no_entries")}
          </div>
        ) : (
          <div className="space-y-1.5 sm:space-y-2">
            {data.entries.map((e) => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: Math.min(e.rank * 0.015, 0.4) }}
                className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl transition ${
                  e.is_me
                    ? "bg-primary/10 border-2 border-primary shadow-lg"
                    : "bg-white shadow-sm"
                }`}
              >
                <RankBadge rank={e.rank} />
                <Avatar name={e.name} rank={e.rank} />
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-bold truncate ${
                      e.is_me ? "text-primary" : "text-body"
                    } text-sm sm:text-base`}
                  >
                    {e.name}
                  </div>
                  <div className="text-[10px] sm:text-xs text-muted">
                    {t("learner:level")} {e.current_level}
                  </div>
                </div>
                <div
                  className={`font-bold text-sm sm:text-base shrink-0 ${
                    e.is_me ? "text-primary" : "text-muted"
                  }`}
                >
                  {e.xp} XP
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3 sm:px-4 py-2 rounded-xl font-bold text-xs sm:text-sm transition ${
        active
          ? "bg-primary text-white shadow"
          : "bg-transparent text-muted hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function RankBadge({ rank }: { rank: number }) {
  const base =
    "w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm shrink-0";
  if (rank === 1) return <div className={`${base} bg-yellow-400 text-white shadow`}>1</div>;
  if (rank === 2) return <div className={`${base} bg-gray-300 text-white shadow`}>2</div>;
  if (rank === 3) return <div className={`${base} bg-orange-400 text-white shadow`}>3</div>;
  return <div className={`${base} bg-gray-100 text-muted`}>{rank}</div>;
}

function Avatar({ name, rank }: { name: string; rank: number }) {
  const initial = (name?.[0] ?? "?").toUpperCase();
  const color =
    rank === 1
      ? "bg-gradient-to-br from-yellow-300 to-yellow-500"
      : rank === 2
      ? "bg-gradient-to-br from-gray-300 to-gray-500"
      : rank === 3
      ? "bg-gradient-to-br from-orange-300 to-orange-500"
      : "bg-gradient-to-br from-sakura to-primary/70";
  return (
    <div
      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-white text-sm sm:text-base shrink-0 ${color}`}
    >
      {initial}
    </div>
  );
}
